/**
 * "Generate themes" review dialog (B3) — runs the KS `generate_themes` pipeline
 * (B1/B2) over the project's recordings (or `storyIds`) and lists the returned
 * candidates. Picking one hands a prefilled draft up to the parent, which opens
 * the normal ThemeModal so the user edits and saves (create is unchanged).
 * Nothing is written until the user saves.
 *
 * F17: suggestions are kept in the browser per user + project + recording scope
 * (themeSuggestionsStore), so closing the dialog doesn't lose them. Reopening
 * shows them; each can be dismissed; saved ones show as saved. "Generate more"
 * skips everything shown, dismissed or already saved, and appends the new ones.
 * The run itself is app-level (qce slice), so the dialog can close mid-run and a
 * toast reports when suggestions are ready.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Chip, CircularProgress, Dialog, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import TSPrimaryButton from "../../../../components/common/buttons/TSPrimaryButton";
import TSSecondaryButton from "../../../../components/common/buttons/TSSecondaryButton";
import type { RootState } from "../../../../store/state/rootReducer";
import type { ThemeCandidate } from "../../../../store/state/qce/agentService";
import { runGenerateThemes, useQceDispatch, useThemes } from "../../../../store/state/qce/qceSlice";

import {
  clearSuggestions,
  loadSuggestions,
  normalizeThemeName,
  saveSuggestions,
  suggestionsStorageKey,
} from "./themeSuggestionsStore";
import type { StoredSuggestions } from "./themeSuggestionsStore";
import type { ThemeDraft } from "./types";

interface GenerateThemesModalProps {
  orgId: string;
  projectId: string;
  /** Generate from these recordings only (omit = the whole project). */
  storyIds?: string[];
  /** Describes the `storyIds` scope, e.g. "In this recording" / "In 3 selected recordings". */
  scopeLabel?: string;
  /** Open the candidate in ThemeModal to review + edit before saving. */
  onPick: (draft: ThemeDraft) => void;
  /** One-click create the candidate as-is (C2w). */
  onQuickAdd: (draft: ThemeDraft) => void;
  onClose: () => void;
}

function candidateToDraft(c: ThemeCandidate): ThemeDraft {
  return {
    name: c.name,
    description: c.description,
    keywords: (c.keywords || []).join(", "),
    exemplars: c.exemplars || [],
    skill: c.skill || "",
  };
}

export default function GenerateThemesModal({
  orgId,
  projectId,
  storyIds,
  scopeLabel,
  onPick,
  onQuickAdd,
  onClose,
}: GenerateThemesModalProps) {
  const dispatch = useQceDispatch();
  const userId = useSelector((s: RootState) => s.auth?.user_id) as string | undefined;
  const storageKey = suggestionsStorageKey(userId || "anonymous", projectId, storyIds);
  const run = useSelector((s: RootState) => s.qce.generateRuns[storageKey]);
  const isRunning = run?.status === "running";
  const { themes, loading: themesLoading } = useThemes(projectId);

  const [stored, setStored] = useState<StoredSuggestions>(() => loadSuggestions(storageKey));
  const hasAutoRun = useRef(false);

  const savedNames = useMemo(() => new Set(themes.map((t) => normalizeThemeName(t.name))), [themes]);
  const dismissed = useMemo(() => new Set(stored.dismissed.map(normalizeThemeName)), [stored.dismissed]);
  const visible = stored.candidates.filter((c) => !dismissed.has(normalizeThemeName(c.name)));
  const isSaved = (c: ThemeCandidate) => savedNames.has(normalizeThemeName(c.name));

  // Skip everything already shown, dismissed or saved in the project.
  const generate = () => {
    if (!orgId) return;
    const skipNames = Array.from(
      new Set([...stored.candidates.map((c) => c.name), ...stored.dismissed, ...themes.map((t) => t.name)])
    );
    void dispatch(runGenerateThemes({ orgId, projectId, storyIds, storageKey, skipNames, scopeLabel }));
  };

  // Nothing kept for this scope → generate once, after the saved themes load (so they're skipped).
  useEffect(() => {
    if (hasAutoRun.current || themesLoading || !orgId) return;
    hasAutoRun.current = true;
    if (!stored.candidates.length && !isRunning) generate();
  }, [themesLoading, orgId]);

  // A run finished (here or after the dialog was closed and reopened): pick up its results.
  useEffect(() => {
    if (run?.status === "done") setStored(loadSuggestions(storageKey));
  }, [run?.status, storageKey]);

  // Once every suggestion is saved or dismissed, stop keeping them.
  useEffect(() => {
    const isAllHandled =
      stored.candidates.length > 0 &&
      stored.candidates.every((c) => isSaved(c) || dismissed.has(normalizeThemeName(c.name)));
    if (isAllHandled) clearSuggestions(storageKey);
  }, [stored, savedNames, dismissed, storageKey]);

  const dismiss = (c: ThemeCandidate) => {
    const next = { ...stored, dismissed: [...stored.dismissed, c.name] };
    setStored(next);
    saveSuggestions(storageKey, next);
  };

  const isEmptyResult = run?.status === "done" && visible.length === 0;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <Box sx={{ p: "24px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="4px">
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#212427" }}>Generate themes</Typography>
          <IconButton onClick={onClose} size="small" sx={{ "&:focus": { outline: "none" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: "14px", color: "#606971", mb: scopeLabel ? "8px" : "16px" }}>
          Suggested themes from {storyIds?.length ? "these recordings" : "this project's recordings"}. Pick one to
          review and save.
        </Typography>
        {scopeLabel && (
          <Typography
            sx={{
              display: "inline-block",
              fontSize: "13px",
              fontWeight: 600,
              color: "#1A7E70",
              backgroundColor: "#F0FAF7",
              borderRadius: "6px",
              px: "8px",
              py: "2px",
              mb: "16px",
            }}
          >
            {scopeLabel}
          </Typography>
        )}

        {isRunning && visible.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" py="40px" gap="12px">
            <CircularProgress size={26} sx={{ color: "#1A7E70" }} />
            <Typography sx={{ fontSize: "14px", color: "#606971" }}>Analyzing recordings…</Typography>
          </Box>
        )}

        {run?.status === "error" && (
          <Box py="16px" textAlign="center">
            <Typography sx={{ fontSize: "14px", color: "#B91C1C", mb: "12px" }}>{run.error}</Typography>
            <TSSecondaryButton text="Try again" onClick={generate} />
          </Box>
        )}

        {isEmptyResult && (
          <Typography sx={{ fontSize: "14px", color: "#606971", py: "24px", textAlign: "center" }}>
            No new themes found — try a different set of recordings.
          </Typography>
        )}

        {visible.length > 0 && (
          <Box display="flex" flexDirection="column" gap="12px" sx={{ maxHeight: 420, overflow: "auto" }}>
            {visible.map((c) => {
              const saved = isSaved(c);
              return (
                <Box
                  key={c.name}
                  onClick={() => !saved && onPick(candidateToDraft(c))}
                  role="button"
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "10px",
                    p: "14px",
                    cursor: saved ? "default" : "pointer",
                    opacity: saved ? 0.7 : 1,
                    "&:hover": saved ? undefined : { borderColor: "#1A7E70", backgroundColor: "#F0FAF7" },
                  }}
                >
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap="8px" mb="4px">
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#212427" }}>{c.name}</Typography>
                    <Box display="flex" alignItems="center" gap="4px" flexShrink={0}>
                      {saved ? (
                        <Chip
                          label="Saved"
                          size="small"
                          sx={{ fontSize: "12px", fontWeight: 700, backgroundColor: "#F0FAF7", color: "#1A7E70" }}
                        />
                      ) : (
                        <>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              onQuickAdd(candidateToDraft(c));
                            }}
                            size="small"
                            variant="contained"
                            sx={{
                              textTransform: "none",
                              fontSize: "13px",
                              fontWeight: 700,
                              borderRadius: "4px",
                              backgroundColor: "#1A7E70",
                              "&:hover": { backgroundColor: "#187266" },
                              "&:focus": { outline: "none" },
                            }}
                          >
                            Add
                          </Button>
                          <Tooltip title="Dismiss">
                            <IconButton
                              size="small"
                              aria-label={`Dismiss ${c.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                dismiss(c);
                              }}
                              sx={{ "&:focus": { outline: "none" } }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "13px", color: "#606971", mb: "8px" }}>{c.description}</Typography>
                  <Box display="flex" flexWrap="wrap" gap="6px">
                    {(c.keywords || []).slice(0, 6).map((k, ki) => (
                      <Chip key={ki} label={k} size="small" sx={{ fontSize: "12px", backgroundColor: "#F1F2F3" }} />
                    ))}
                    {c.evidence?.length ? (
                      <Chip
                        label={`${c.evidence.length} examples`}
                        size="small"
                        sx={{ fontSize: "12px", backgroundColor: "#E7F1FF", color: "#1D4ED8" }}
                      />
                    ) : null}
                  </Box>
                  {!saved && (
                    <Typography sx={{ fontSize: "12px", color: "#9AA1A8", mt: "8px" }}>
                      Click to review &amp; edit
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Footer: Generate more next to Close. */}
        <Box display="flex" alignItems="center" gap="12px" mt="20px">
          <Typography sx={{ flex: 1, fontSize: "12px", color: "#606971" }}>
            {isRunning ? "You can close this — we'll let you know when it's done." : ""}
          </Typography>
          <TSSecondaryButton text="Close" onClick={onClose} />
          <TSPrimaryButton
            text={isRunning ? "Generating…" : "Generate more"}
            onClick={generate}
            loading={isRunning}
            disabled={isRunning || !orgId}
          />
        </Box>
      </Box>
    </Dialog>
  );
}
